import { CustomerOrder } from "src/interfaces/order.interface";

export interface EnterprisePackageSlipData {
  orderNumber: string;
  deliveryDate?: string;
  customer: string;
  companySite?: string;
  addressLines: string[];
  pickupLocation?: string;
  orderId: string;
}

const EMPTY_LABEL = "-";

export function buildEnterprisePackageSlipData(order: CustomerOrder): EnterprisePackageSlipData {
  return {
    orderNumber:
      text(order.currentDayOrderCount) ??
      (order.correlationId.length <= 8 ? order.correlationId : order.correlationId.slice(0, 8)),
    deliveryDate: formatDate(order.enterpriseDeliveryAt ?? order.scheduledAt),
    customer: customerLabel(order),
    companySite: companySiteLabel(order),
    addressLines: enterpriseAddressLines(order),
    pickupLocation: text(order.enterprisePickupLocation),
    orderId: order.correlationId,
  };
}

export function chunkOrders<T>(orders: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < orders.length; i += size) {
    chunks.push(orders.slice(i, i + size));
  }
  return chunks;
}

function customerLabel(order: CustomerOrder): string {
  const name = [text(order.customerFirstName), text(order.customerLastName)].filter(Boolean).join(" ");
  if (name) return name;
  return text(order.customerPhoneNumber) ?? EMPTY_LABEL;
}

function companySiteLabel(order: CustomerOrder): string | undefined {
  const company = text(order.enterpriseCompanyName) ?? text(order.channelName);
  const site = text(order.enterpriseSiteName);
  if (company && site) return `${company} / ${site}`;
  return company ?? site;
}

function enterpriseAddressLines(order: CustomerOrder): string[] {
  const line1 = text(order.enterpriseDeliveryAddressLine1) ?? text(order.deliveryAddressLine1);
  const line2 = text(order.enterpriseDeliveryAddressLine2) ?? text(order.deliveryAddressLine2);
  const city = text(order.enterpriseDeliveryCity) ?? text(order.deliveryCity);
  const state = text(order.enterpriseDeliveryState) ?? text(order.deliveryState);
  const zip = text(order.enterpriseDeliveryZipcode) ?? text(order.deliveryZipcode);
  const region = [state, zip].filter(Boolean).join(" ");
  const cityStateZip = [city, region || undefined].filter(Boolean).join(", ");
  return [line1, line2, cityStateZip || undefined].filter((line): line is string => Boolean(line));
}

function text(value: unknown): string | undefined {
  const normalized = value == null ? "" : String(value).trim();
  return normalized || undefined;
}

function formatDate(value: number | string | null | undefined): string | undefined {
  if (value == null || value === "") return undefined;
  const date = typeof value === "number" ? new Date(value) : parseDateString(value);
  if (!date || Number.isNaN(date.getTime())) return undefined;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function parseDateString(value: string): Date | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const asNumber = Number(trimmed);
  if (Number.isFinite(asNumber)) return new Date(asNumber);
  return new Date(trimmed);
}
